// src/frontend/src/api/index.js

import { track } from "../analytics";
export { cachedFetch, invalidateCache } from "./cache";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

/**
 * Thrown for any request that did not come back with data.
 *
 * `status` is the HTTP status, or `0` when the request never reached a server
 * at all — offline, DNS failure, a blocked origin. Callers that need to tell a
 * legitimate "not found" from an outage read it; most just need to know the
 * call failed.
 */
export class ApiError extends Error {
  constructor(status, message, options) {
    super(message, options);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * One place where a request either produces data or throws.
 *
 * This file used to catch everything and return `[]`. It made the app look
 * healthy while it was blind: a database outage rendered as "no upcoming
 * collections", the resident left their bin indoors, and the error branch every
 * page had already written could never run. That was F11, and the earlier
 * agent instruction it came from — "always handle errors gracefully, return []
 * or null, never crash" — is explicitly retired in CLAUDE.md.
 *
 * The admin client (./admin.ts) has always worked this way. This brings the
 * public one in line: **an empty array means the server said there is nothing.
 * It never means the request failed.**
 */
async function request(path, init) {
  let response;

  // The path without its query string: a search term is the resident's own
  // words and has no business in an analytics row.
  const endpoint = `${API_BASE_URL}${path}`.split("?")[0];

  try {
    response = await fetch(`${API_BASE_URL}${path}`, init);
  } catch (error) {
    // fetch only rejects when the request never completed, so this is a
    // transport failure rather than anything the server said. Status 0 says
    // exactly that, and distinguishes "offline at the bins" from "our server
    // answered with a problem" in the numbers.
    track("error_api", { endpoint, status: 0 });
    throw new ApiError(0, "Could not reach the server.", { cause: error });
  }

  if (!response.ok) {
    track("error_api", { endpoint, status: response.status });
    throw new ApiError(
      response.status,
      `Request failed (HTTP ${response.status})`,
    );
  }

  return response.json();
}

// ============================================
// READS
// ============================================

export const fetchCalendarEvents = async (year) =>
  request(`/calendar?year=${encodeURIComponent(year)}`);

/**
 * Upcoming events from `from` (a `YYYY-MM-DD` string) onward, spanning a
 * year boundary. Home needs this instead of `fetchCalendarEvents` — a
 * year-scoped fetch goes empty in late December once the current year's
 * calendar is exhausted.
 */
export const fetchUpcomingCalendarEvents = async (from, limit = 10) =>
  request(
    `/calendar/upcoming?from=${encodeURIComponent(from)}&limit=${encodeURIComponent(limit)}`,
  );

export const fetchWasteTypes = async () => request("/waste-types");

/**
 * One waste type, or `null` when there is no such type.
 *
 * The 404 is an answer, not a failure — the detail page turns it into "waste
 * type not found". Every other status still throws, so a 500 cannot masquerade
 * as "this waste type does not exist".
 */
export const fetchWasteTypeById = async (id) => {
  try {
    return await request(`/waste-type/${encodeURIComponent(id)}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

export const fetchCollectionPoints = async () => request("/collection-points");

export const fetchFees = async () => request("/fees");

export const fetchSpecialEvents = async () => request("/special-events");

export const fetchSalesPoints = async () => request("/sales-points");

export const fetchServiceProviders = async () => request("/service-providers");

// ============================================
// FEEDBACK
// ============================================

export const submitFeedback = async (payload) =>
  request("/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

// ============================================
// PUSH
// ============================================

/**
 * The VAPID public key the browser must subscribe with.
 *
 * Fetched rather than baked into the bundle: a build-time copy can drift from
 * whatever the server actually signs with, and every push then fails with a
 * 403 that nothing surfaces.
 *
 * The "Push is not available" wording is load-bearing — `useNotifications.ts`
 * matches on it to tell a misconfigured deployment apart from a user who
 * declined the permission prompt.
 */
export const fetchVapidPublicKey = async () => {
  let publicKey;

  try {
    ({ publicKey } = await request("/push/public-key"));
  } catch (error) {
    const status = error instanceof ApiError ? error.status : 0;
    throw new ApiError(status, `Push is not available (HTTP ${status})`, {
      cause: error,
    });
  }

  if (!publicKey) {
    throw new ApiError(
      200,
      "Push is not available: the server returned no key.",
    );
  }
  return publicKey;
};

const jsonPost = (path, body) =>
  request(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const subscribeUser = async (subscription, preferences) =>
  jsonPost("/subscribe", { subscription, preferences });

export const unsubscribeUser = async (subscription) =>
  jsonPost("/unsubscribe", subscription);

// NOTE: sendNotification API removed for security.
// Push notifications are sent only via server-side scheduled function.

// ============================================
// SCAN
// ============================================

export const analyzeImageWithCloudVision = async (imageDataUrl) =>
  jsonPost("/analyze-image", { imageData: imageDataUrl });

/**
 * Categorize waste using an LLM when local matching fails.
 *
 * **This route does not exist** — see F08. Every call 404s, and ScanPage's
 * catch falls through to "Hauskehricht", which is how the scanner ends up
 * confidently telling people to landfill recyclables. Kept here only because
 * removing the caller is part of the scan rework.
 *
 * @param {string[]} visionLabels - Labels from Cloud Vision
 * @param {string} objectLabel - Primary object label
 * @returns {Promise<{wasteId: string, confidence: number, preparation: string}>}
 */
export const categorizeWasteWithLLM = async (visionLabels, objectLabel = "") =>
  jsonPost("/categorize-waste", { visionLabels, objectLabel });
